import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Field, getFormValues } from "redux-form";
import { ReduxControls } from "rhombus-react";

import { SOCIAL_WIZARD } from "widgets/forms/list";

import {
	dynamicMapStringToOption,
	dynamicNormalizeOptionToString,
	nonDynamicFields
} from "social/utils";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { FACEBOOK_IMAGE_CONSTRAINTS } from "social/validators/facebook";
import DynamicInput from "social/DynamicInput";
import DynamicSelect from "social/DynamicSelect";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";
import { noop } from "utils/common";
import { FB_CTA_OPTIONS } from "social/constants/CTA";
import FacebookDisclaimer from "social/FacebookDisclaimer";

import "./FacebookMainContent.css";

const { MAX_LENGTH_LANDING_PAGE } = FACEBOOK_IMAGE_CONSTRAINTS;

export const FACEBOOK_CAROUSEL_FIELDS = {
	creativeName: {
		name: "name",
		label: "Creative Name",
		className: "full-width cypress-creative-name",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
		required: true,
	},
	primaryText: {
		name: "primaryText",
		label: "Primary Text",
		className: "full-width cypress-primary-text",
		placeholder: "Tell people what your creative is about",
		tip: {
			description: "May get truncated after 125 characters",
			direction: "right",
		},
		component: DynamicInput,
		type: "textarea",
		defaultDynVarName: "copy",
	},
	disclaimer: {
		name: "disclaimerEnabled",
		component: FacebookDisclaimer,
	},
	cta: {
		name: "cta",
		label: "CTA",
		className: "half-width cypress-cta-select",
		placeholder: "Enter Call to Action Text",
		component: DynamicSelect,
		defaultDynVarName: "cta",
		options: FB_CTA_OPTIONS,
		normalize: dynamicNormalizeOptionToString,
		format: dynamicMapStringToOption,
		isClearable: false,
		required: true,
		onMenuScrollToBottom: noop,
		variableNameInputTip: "Edit variable name",
	},
	seeMoreCard: {
		name: "seeMoreCard",
		label: "Display a \"See More\" card at the end",
		className: "half-width cypress-see-more",
		component: ReduxControls.Checkbox,
		type: "checkbox",
		...nonDynamicFields(),
	},
	seeMoreDisplayLink: {
		name: "seeMoreDisplayLink",
		label: "See More Display Link",
		className: "full-width cypress-see-more-display-link",
		placeholder: "Enter link text to be displayed",
		component: DynamicInput,
		defaultDynVarName: "see_more_display_link",
		charMax: MAX_LENGTH_LANDING_PAGE,
		// showCharCount: true,
	},
	seeMoreDisplayUrl: {
		name: "seeMoreDisplayUrl",
		label: "See More URL",
		className: "full-width cypress-see-more-url",
		placeholder: "https://www.example.com",
		component: DynamicInput,
		defaultDynVarName: "see_more_url",
		charMax: MAX_LENGTH_LANDING_PAGE,
		// showCharCount: true,
		required: true,
	},
	sponsorPageId: {
		name: "sponsorPageId",
		label: "Sponsor Page ID",
		className: "full-width cypress-sponsor-page",
		placeholder: "Enter Sponsor Facebook Page ID",
		component: ReduxControls.Input,
		...nonDynamicFields(),
	},
};

function fieldGenerator(props) {
	const {
		adType,
		seeMoreCard,
		showPristineErrors,
		isViewOnly,
		doVersionsExist,
	} = props;

	const seeMoreCardFields = ["seeMoreDisplayLink", "seeMoreDisplayUrl"];

	return (name) => {
		const fieldProps = FACEBOOK_CAROUSEL_FIELDS[name];

		let extraProps = {
			showPristineErrors,
			disabled: isViewOnly,
		};

		if(extraProps.disabled && fieldProps.placeholder){
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = "";
		}

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			if (fieldProps.name !== "cta") {
				extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
			}
			if(fieldProps.name === "seeMoreCard") {
				extraProps.disabled = true;
			}
		}

		if(!seeMoreCard?.value && seeMoreCardFields.includes(name)){
			return;
		}

		if(name === "disclaimer"){
			extraProps.adType = adType;
		}

		return (
			<Field
				key={fieldProps.name}
				{...fieldProps}
				{...extraProps}
			/>
		);
	};
}

export default function FacebookMainContent(props) {
	const {
		adType,
		isViewOnly,
		showPristineErrors = false,
		doVersionsExist,
	} = props;

	const formValues = useSelector(state => getFormValues(SOCIAL_WIZARD)(state)?.[adType] || {});

	const { seeMoreCard } = formValues;

	const generateFields = fieldGenerator({
		adType,
		seeMoreCard,
		isViewOnly,
		showPristineErrors,
		doVersionsExist,
	});
	const fields = useMemo(() => (
		Object.keys(FACEBOOK_CAROUSEL_FIELDS).map(generateFields)
	), [adType, seeMoreCard]);

	return (
		<div className="form-container">
			<div className="fields">
				{ fields }
			</div>
		</div>
	);
}
